import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  FileText,
  Printer,
  AlertCircle,
  XCircle
} from 'lucide-react';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { useClaimStore } from '../store/claimStore';
import { useScreenNavigation } from '../hooks/useScreenNavigation';
import { getCoverageCodeLabel } from '../data/coverageCodes';
import { EVENT_DATE_ROUTE } from '../utils/eventDateScreenVisibility';
import { logger } from '../utils/logger';

/**
 * Parse a date string (YYYY-MM-DD) as a local date to avoid timezone issues.
 * Using new Date(dateString) interprets the string as UTC, which can shift dates.
 */
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const SCREEN_CODE = '05-03-FIN';

const ConfirmationScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useScreenNavigation(SCREEN_CODE);
  const {
    stepData,
    submitClaim,
    saveDraft,
    currentClaim,
    eventNumber
  } = useClaimStore();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStartedSubmission, setHasStartedSubmission] = useState(false);

  // Submission modal states
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState({
    status: 'submitting' as 'submitting' | 'success' | 'error',
    message: '',
    errorDetails: ''
  });

  // Extract claim data from store
  // Claim type is from 02-00-GARANTIE screen
  const selectedClaimType = stepData['02-00-GARANTIE']?.selectedType || 'medical';
  const claimType = selectedClaimType.toUpperCase(); // Convert to uppercase for display labels

  // Get beneficiary data
  // Priority: 1. Contact preferences (01-00-DEMANDEUR) if beneficiary = client
  // 2. Beneficiary screen (02M-01-BENEFICIAIRE for Medical/Cancellation/Baggage flows)
  const contactPreferences = stepData['01-00-DEMANDEUR'] || {};
  const beneficiaryData = stepData['02M-01-BENEFICIAIRE'] || stepData['02A-01-BENEFICIAIRE'] || stepData['02B-01-BENEFICIAIRE'] || {};

  let beneficiaries = [];

  // Extract beneficiaries from the beneficiaries array (not selectedBeneficiaries)
  const allBeneficiaries = beneficiaryData.beneficiaries || [];

  // Filter selected beneficiaries
  const selectedBeneficiaries = allBeneficiaries.filter((b: any) => b.selected);

  // Check if we have explicit beneficiary selection
  if (selectedBeneficiaries.length > 0) {
    beneficiaries = selectedBeneficiaries;
  }
  // Check if beneficiary = client (from contact preferences)
  else if (beneficiaryData.beneficiaryType === 'client' || contactPreferences.firstName) {
    beneficiaries = [{
      firstName: contactPreferences.firstName || '',
      lastName: contactPreferences.lastName || ''
    }];
  }

  // Get expense data from expense validation screen (04-02-VALIDATION)
  const expenseData = stepData['04-02-VALIDATION'] || {};
  const expenses = expenseData.expenses || [];
  const subtotalAmount = expenseData.subtotalAmountCAD || 0;
  const refundReceivedAmount = expenseData.refundReceivedAmount || 0;
  const totalAmount = expenseData.totalAmountCAD || 0;

  // Get all uploaded documents (invoices 04-01-FACTURES + supporting docs 04-03-JUSTIFICATIFS)
  const invoiceData = stepData['04-01-FACTURES'] || {};
  const supportingData = stepData['04-03-JUSTIFICATIFS'] || {};

  const invoices = invoiceData.documents || invoiceData.uploadedFiles || [];
  const supportingDocs = supportingData.documents || supportingData.uploadedFiles || [];
  const allDocuments = [...invoices, ...supportingDocs];

  // Use event number from store (auto-generated by Salesforce)
  const caseNumber = eventNumber || currentClaim?.eventNumber || 'N/A';

  // Submit claim on mount
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const submitClaimAsync = async () => {
      // Triple-check to prevent double submission
      const confirmationData = stepData['05-03-FIN'] || {};

      // Check 1: Already fully submitted and completed
      if (confirmationData.submissionCompleted) {
        return;
      }

      // Check 2: Submission already started (in stepData - survives remount)
      if (confirmationData.claimSubmitted) {
        return;
      }

      // Check 3: Submission already started (local state - same mount only)
      if (hasStartedSubmission) {
        return;
      }

      if (!isSubmitting) {
        setHasStartedSubmission(true);
        setIsSubmitting(true);

        // Show submission modal immediately
        setSubmissionProgress({
          status: 'submitting',
          message: '',
          errorDetails: ''
        });
        setShowSubmissionModal(true);

        // Mark claim as submitted IMMEDIATELY to prevent back navigation during API call
        const { updateClaimData } = useClaimStore.getState();
        updateClaimData({ claimSubmitted: true }, '05-03-FIN');

        // DEV-8836: the draft was abandoned/deleted in another tab. ClaimNotEditableModal (App)
        // explains it and sends the user home — no submission error modal on top of it.
        const handOverToClaimNotEditableModal = () => {
          if (!useClaimStore.getState().claimNotEditable) {
            return false;
          }
          setShowSubmissionModal(false);
          return true;
        };

        try {
          // IMPORTANT: Save draft first to update all data in the backend
          await saveDraft();
          if (handOverToClaimNotEditableModal()) {
            return;
          }

          // Then submit the claim
          await submitClaim();

          // Update modal to success state
          setSubmissionProgress({
            status: 'success',
            message: t('steps.confirmation.submission.success'),
            errorDetails: ''
          });

          // Mark submission as completed to prevent re-submission on remount
          updateClaimData({ submissionCompleted: true }, '05-03-FIN');

          // Auto-close modal after 2 seconds to show success
          setTimeout(() => {
            setShowSubmissionModal(false);
          }, 2000);
        } catch (error: any) {
          if (handOverToClaimNotEditableModal()) {
            return;
          }
          logger.error('Claim submission error', {
            errorType: 'ClaimSubmissionError',
            screen: 'ConfirmationScreen'
          }, error);

          // Update modal to error state
          setSubmissionProgress({
            status: 'error',
            message: t('steps.confirmation.submission.error'),
            errorDetails: error?.message || error?.toString() || ''
          });

          // On error, clear the submitted flag so user can go back and retry
          updateClaimData({ claimSubmitted: false }, '05-03-FIN');
        } finally {
          setIsSubmitting(false);
        }
      }
    };
    submitClaimAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuit = () => {
    // Clear ALL claim data (complete reset for new claim)
    const { clearClaim } = useClaimStore.getState();
    clearClaim();

    // Navigate to home/welcome screen
    navigate('/');
  };

  const handleSubmitAnother = () => {
    // Clear all data from section 02 onwards (keep 00-Entry and 01-Contract/Contact)
    // This ensures a fresh claim is created (POST) instead of updating the old one (PUT).
    // ADR-050 as amended by DEV-7682: the event date and the eligibility verdict computed
    // at that date are dropped too — see `clearClaimFromSection`.
    const { clearClaimFromSection } = useClaimStore.getState();
    clearClaimFromSection('02');

    // …so the next claim starts by asking its OWN event date instead of jumping straight
    // to the coverage. The contract and the trip are still reused: 00-03-EVENEMENT leads
    // back into 01-00-DEMANDEUR / 01-01-CONTRAT prefilled, and eligibility is re-queried
    // at the new date on the way to 02-00-GARANTIE.
    navigate(EVENT_DATE_ROUTE);
  };

  const handlePrint = () => {
    window.print();
  };

  // Format claim type for display
  const getClaimTypeLabel = (type: string): string => {
    return t(`claimTypes.${type}`, type);
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 print:hidden">
            <CheckCircle className="w-12 h-12 text-green-600" aria-hidden="true" />
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            {t('steps.confirmation.pageTitle')}
          </h1>
        </div>

        {/* Claim Summary Card */}
        <div className="card mb-6 print:border-2 print:border-gray-300">
          <div className="card-header bg-gradient-to-r from-tenant-50 to-tenant-100 print:bg-white">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {t('steps.confirmation.sectionTitle')}
              </h2>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 text-tenant-600 hover:text-tenant-800 text-sm font-medium print:hidden"
                aria-label={t('steps.confirmation.printButton')}
              >
                <Printer className="w-5 h-5" />
                {t('steps.confirmation.printButton')}
              </button>
            </div>
          </div>

          <div className="card-body space-y-6">
            {/* Case Number */}
            <div className="pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mb-1">
                {t('steps.confirmation.caseNumberLabel')}
              </p>
              <p className="text-2xl font-bold text-tenant-600" role="status">
                {caseNumber}
              </p>
            </div>

            {/* Claim Type */}
            <div className="pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mb-1">
                {t('steps.confirmation.claimTypeLabel')}
              </p>
              <p className="text-lg font-semibold text-gray-900">
                {getClaimTypeLabel(claimType)}
              </p>
            </div>

            {/* Beneficiary */}
            <div className="pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mb-1">
                {t('steps.confirmation.beneficiaryLabel')}
              </p>
              <div className="space-y-2">
                {beneficiaries.length > 0 ? (
                  beneficiaries.map((beneficiary: { firstName?: string; lastName?: string }, index: number) => (
                    <p key={index} className="text-base font-medium text-gray-900">
                      {beneficiary.firstName} {beneficiary.lastName}
                      {beneficiaries.length > 1 && (
                        <span className="text-gray-500 text-sm ml-2">
                          ({t('steps.confirmation.beneficiaryNumber')} {index + 1})
                        </span>
                      )}
                    </p>
                  ))
                ) : (
                  <p className="text-base text-gray-500 italic">
                    {t('steps.confirmation.notSpecified')}
                  </p>
                )}
              </div>
            </div>

            {/* Fees and Amounts */}
            <div className="pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                {t('steps.confirmation.expensesLabel')}
              </p>
              {expenses.length > 0 ? (
                <div className="space-y-3">
                  {expenses.map((expense: {
                    coverageCode?: string;
                    description?: string;
                    serviceStartDate?: string;
                    serviceEndDate?: string;
                    serviceProvider?: string;
                    amountPaid?: number;
                    currency?: string;
                    amountCAD?: number;
                  }, index: number) => {
                    // For medical workflow: use coverage code label
                    // For cancellation/baggage workflows: use expense description
                    let expenseLabel: string;
                    if (selectedClaimType === 'medical' && expense.coverageCode) {
                      expenseLabel = getCoverageCodeLabel(expense.coverageCode, i18n.language);
                    } else if (expense.description) {
                      expenseLabel = expense.description;
                    } else {
                      expenseLabel = `${t('steps.confirmation.expenseNumber')} #${index + 1}`;
                    }

                    return (
                    <div key={index} className="flex justify-between items-start bg-gray-50 p-3 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {expenseLabel}
                        </p>
                        {expense.serviceProvider && (
                          <p className="text-sm text-gray-600">
                            {expense.serviceProvider}
                          </p>
                        )}
                        {expense.serviceStartDate && (
                          <p className="text-sm text-gray-600">
                            {parseLocalDate(expense.serviceStartDate).toLocaleDateString()}
                            {expense.serviceEndDate && expense.serviceEndDate !== expense.serviceStartDate && (
                              <> - {parseLocalDate(expense.serviceEndDate).toLocaleDateString()}</>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-bold text-gray-900">
                          ${(expense.amountCAD || 0).toFixed(2)} CAD
                        </p>
                        {expense.currency && expense.currency !== 'CAD' && expense.amountPaid && (
                          <p className="text-sm text-gray-600">
                            ({expense.amountPaid.toFixed(2)} {expense.currency})
                          </p>
                        )}
                      </div>
                    </div>
                  );
                  })}

                  {/* Show breakdown if refund exists */}
                  {refundReceivedAmount > 0 ? (
                    <div className="space-y-2 pt-3 border-t-2 border-gray-300">
                      {/* Subtotal */}
                      <div className="flex justify-between items-center">
                        <p className="text-base font-semibold text-gray-700">
                          {t('steps.confirmation.subtotalLabel')}
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                          ${subtotalAmount.toFixed(2)} CAD
                        </p>
                      </div>

                      {/* Refund Received */}
                      <div className="flex justify-between items-center">
                        <p className="text-base font-semibold text-gray-700">
                          {t('steps.confirmation.refundLabel')}
                        </p>
                        <p className="text-base font-semibold text-red-600">
                          -${refundReceivedAmount.toFixed(2)} CAD
                        </p>
                      </div>

                      {/* Net Total */}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <p className="text-lg font-bold text-gray-900">
                          {t('steps.confirmation.totalLabel')}
                        </p>
                        <p className="text-xl font-bold text-tenant-600">
                          ${totalAmount.toFixed(2)} CAD
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Show simple total if no refund */
                    <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                      <p className="text-lg font-bold text-gray-900">
                        {t('steps.confirmation.totalLabel')}
                      </p>
                      <p className="text-xl font-bold text-tenant-600">
                        ${totalAmount.toFixed(2)} CAD
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-base text-gray-500 italic">
                  {t('confirmation.noMedicalBillsSubmitted', 'Vous n\'avez soumis aucune facture médicale dont vous demandez le remboursement.')}
                </p>
              )}
            </div>

            {/* Uploaded Documents */}
            <div>
              <p className="text-sm text-gray-600 mb-3">
                {t('steps.confirmation.documentsLabel')}
              </p>
              {allDocuments.length > 0 ? (
                <ul className="space-y-2">
                  {allDocuments.map((doc: any, index: number) => (
                    <li key={index} className="flex items-center gap-3 text-gray-700 min-w-0">
                      <FileText className="w-5 h-5 text-tenant-600 flex-shrink-0" aria-hidden="true" />
                      <span className="text-base break-all min-w-0">
                        {doc.name || doc.fileName || doc.type || doc.fileType || `${t('steps.confirmation.documentNumber')} ${index + 1}`}
                      </span>
                      {doc.category && (
                        <span className="text-sm text-gray-500">
                          ({doc.category})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-base text-gray-500 italic flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" aria-hidden="true" />
                  {t('steps.confirmation.noDocuments')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Message Section */}
        <div className="card mb-6">
          <div className="card-body space-y-4 text-gray-700 leading-relaxed">
            <p className="text-base">
              {t('steps.confirmation.message1')}
            </p>

            <p className="text-base">
              {t('steps.confirmation.message2')}
            </p>

            <p className="text-base">
              {t('steps.confirmation.message3')}
            </p>

            <p className="text-base">
              {t('steps.confirmation.message4')}
            </p>

            <p className="text-lg font-semibold text-tenant-600">
              {t('steps.confirmation.message5')}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4 justify-center items-center mb-8 print:hidden">
          <Button
            variant="primary"
            size="lg"
            onClick={handleQuit}
            className="w-full sm:w-auto sm:min-w-[300px] text-lg py-4"
            aria-label={t('steps.confirmation.exitButton')}
          >
            <span className="font-bold">
              {t('steps.confirmation.exitButton')}
            </span>
          </Button>

          <button
            onClick={handleSubmitAnother}
            className="hidden text-tenant-600 hover:text-tenant-800 text-sm font-normal underline"
            aria-label={t('steps.confirmation.submitAnotherButton')}
          >
            {t('steps.confirmation.submitAnotherButton')}
          </button>
        </div>

        {/* Print-only Footer */}
        <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-600">
          <p>{t('steps.confirmation.printedOn')} {new Date().toLocaleDateString()}</p>
          <p className="mt-1">
            {t('steps.confirmation.portalName')}
          </p>
        </div>
      </div>

      {/* Submission Progress Modal */}
      <Modal
        isOpen={showSubmissionModal}
        onClose={() => submissionProgress.status !== 'submitting' && setShowSubmissionModal(false)}
        title={t('steps.confirmation.submission.modalTitle')}
        size="md"
        footer={
          submissionProgress.status === 'error' ? (
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowSubmissionModal(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
              >
                {t('steps.confirmation.submission.retry')}
              </Button>
            </div>
          ) : null
        }
      >
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-center py-6">

            {/* État 1: Soumission en cours */}
            {submissionProgress.status === 'submitting' && (
              <>
                <div className="relative">
                  <LoadingSpinner size="lg" />
                </div>
                <p className="mt-4 text-base font-medium text-gray-900">
                  {t('steps.confirmation.submission.submitting')}
                </p>
                <p className="mt-2 text-sm text-gray-600 text-center max-w-md">
                  {t('steps.confirmation.submission.pleaseWait')}
                </p>
              </>
            )}

            {/* État 2: Succès */}
            {submissionProgress.status === 'success' && (
              <>
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <p className="mt-4 text-base font-medium text-gray-900">
                  {submissionProgress.message}
                </p>
                <p className="mt-2 text-sm text-gray-600 text-center max-w-md">
                  {t('steps.confirmation.submission.successDetails')}
                </p>
              </>
            )}

            {/* État 3: Erreur */}
            {submissionProgress.status === 'error' && (
              <>
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <p className="mt-4 text-base font-medium text-red-900">
                  {submissionProgress.message}
                </p>
                <p className="mt-2 text-sm text-gray-600 text-center max-w-md">
                  {t('steps.confirmation.submission.errorDetails')}
                </p>
                {submissionProgress.errorDetails && (
                  <div className="mt-3 p-3 bg-red-50 rounded-md w-full">
                    <p className="text-xs text-red-800 font-mono break-words">
                      {submissionProgress.errorDetails}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default ConfirmationScreen;